import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Check, Copy, ExternalLink, Loader2, AlertCircle, Smartphone, Download } from "lucide-react";
import { api } from "../../lib/api";
import { WellKnownEditor } from "../../components/common/WellKnownEditor";

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      data-testid="copy-btn"
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))]"
    >
      {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function Field({ label, value, mono = true, copy = true }) {
  if (!value) return null;
  return (
    <div className="border border-[hsl(var(--kb-border))] bg-white px-3 py-2 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-text-muted))] mb-1">{label}</div>
        <div className={`text-sm ${mono ? "font-mono" : ""} break-all`}>{value}</div>
      </div>
      {copy && <CopyButton value={value} />}
    </div>
  );
}

export default function AdminDomain() {
  const [ctx, setCtx] = useState(null);
  const [domain, setDomain] = useState(null);
  const [pwa, setPwa] = useState(null);
  const [subStatus, setSubStatus] = useState(null);
  const [pendingDomain, setPendingDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pwaForm, setPwaForm] = useState({
    android_package_name: "",
    android_sha256_fingerprint: "",
    ios_team_id: "",
    ios_bundle_id: "",
    theme_color: "#111111",
    splash_color: "#ffffff",
    assetlinks_json: "",
    apple_aasa_json: "",
  });

  const load = useCallback(async () => {
    try {
      const [c, d, p] = await Promise.all([
        api.get("/tenant-context"),
        api.get("/admin/domain"),
        api.get("/admin/pwa"),
      ]);
      setCtx(c.data);
      setDomain(d.data);
      setPwa(p.data);
      setPwaForm((f) => ({ ...f, ...p.data }));
      // Best-effort: only probe subdomain status if the platform thinks it is supported
      if (c.data?.subdomain_supported) {
        try {
          const s = await api.get("/admin/domain/subdomain-status");
          setSubStatus(s.data);
        } catch { /* non-fatal */ }
      } else {
        setSubStatus(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load domain settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDomain = async () => {
    if (!pendingDomain) return;
    setBusy(true);
    try {
      const res = await api.put("/admin/domain", { domain: pendingDomain });
      setDomain(res.data);
      setPendingDomain("");
      toast.success("Domain saved. Add the DNS TXT record below, then click Verify.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save domain");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const res = await api.post("/admin/domain/verify");
      if (res.data?.verified) {
        toast.success(`${res.data.domain} is now active.`);
      } else {
        toast.error(res.data?.hint || "Verification failed. DNS TXT record not found yet — propagation can take 5-30 minutes.");
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect your custom domain? The storefront will revert to the platform URL.")) return;
    try {
      await api.delete("/admin/domain");
      toast.success("Disconnected");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect");
    }
  };

  const savePwa = async () => {
    setBusy(true);
    try {
      const res = await api.put("/admin/pwa", pwaForm);
      setPwa(res.data);
      toast.success("Mobile app settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const downloadBundle = async () => {
    try {
      const res = await api.get("/admin/pwa/bundle", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ctx?.slug || "storefront"}-mobile-app-bundle.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bundle download failed");
    }
  };

  if (!ctx || !domain) {
    return <div className="p-8 text-[hsl(var(--kb-text-muted))]"><Loader2 className="animate-spin inline w-4 h-4 mr-2" />Loading…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-10" data-testid="admin-domain-page">
      <div>
        <h1 className="text-3xl sm:text-4xl tracking-tight" data-testid="admin-domain-heading">Domain & Mobile App</h1>
        <p className="text-[hsl(var(--kb-text-muted))] mt-2 max-w-2xl">
          Connect your own domain and ship your storefront as a native mobile app on the Play Store and App Store.
        </p>
      </div>

      {/* ---------------- DEFAULT URLs ---------------- */}
      <section className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface))] p-5 sm:p-6" data-testid="storefront-urls">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5" />
          <h2 className="text-xl">Your storefront URLs</h2>
        </div>
        <p className="text-sm text-[hsl(var(--kb-text-muted))] mb-4">
          Every tenant ships with ready-to-use URLs from the platform. You can keep them, or link your own domain below.
        </p>
        <div className="space-y-2">
          <Field label="Path-based (works now)" value={ctx.default_urls.path_based} />
          {ctx.default_urls.subdomain ? (
            <div className="border border-[hsl(var(--kb-border))] bg-white px-3 py-2" data-testid="subdomain-block">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-text-muted))] mb-1 flex items-center gap-2">
                    Subdomain
                    {subStatus?.reachable && subStatus?.matches_platform && (
                      <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded-sm">Live</span>
                    )}
                    {subStatus?.reachable && !subStatus?.matches_platform && (
                      <span className="text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-sm">DNS mismatch</span>
                    )}
                    {subStatus && !subStatus?.reachable && (
                      <span className="text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-sm">Pending DNS</span>
                    )}
                  </div>
                  <div className="text-sm font-mono break-all">{ctx.default_urls.subdomain}</div>
                </div>
                <CopyButton value={ctx.default_urls.subdomain} />
              </div>
              {subStatus?.hint && (
                <div className="mt-2 text-xs text-[hsl(var(--kb-text-muted))]" data-testid="subdomain-hint">
                  {subStatus.hint}
                </div>
              )}
            </div>
          ) : (
            <div className="border border-[hsl(var(--kb-border))] bg-white px-3 py-2 opacity-70" data-testid="subdomain-not-supported">
              <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-text-muted))] mb-1">Subdomain</div>
              <div className="text-sm">
                Not yet enabled on this platform. Super admin needs to configure the platform base domain and wildcard DNS first.
              </div>
            </div>
          )}
          {ctx.default_urls.custom_domain && (
            <Field label="Your custom domain (active)" value={ctx.default_urls.custom_domain} />
          )}
        </div>
      </section>

      {/* ---------------- CUSTOM DOMAIN ---------------- */}
      <section className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface))] p-5 sm:p-6">
        <h2 className="text-xl mb-4">Custom domain</h2>

        {domain.current_domain ? (
          <div className="mb-4 p-3 border border-green-200 bg-green-50 flex items-center gap-3">
            <Check className="w-5 h-5 text-green-700" />
            <div className="flex-1">
              <div className="text-sm font-medium">Active</div>
              <div className="text-sm font-mono">{domain.current_domain}</div>
            </div>
            <a
              href={`https://${domain.current_domain}`} target="_blank" rel="noreferrer"
              className="text-xs underline inline-flex items-center gap-1"
            >Open <ExternalLink className="w-3 h-3" /></a>
            <button
              data-testid="domain-disconnect-btn"
              onClick={disconnect}
              className="text-xs px-3 py-1.5 border border-[hsl(var(--kb-border))] hover:bg-red-50 hover:text-red-700 hover:border-red-200"
            >Disconnect</button>
          </div>
        ) : (
          <div className="mb-4 p-3 border border-yellow-200 bg-yellow-50 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-700 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium">No custom domain connected</div>
              <div className="text-[hsl(var(--kb-text-muted))]">
                Use any domain you already own. Buy one from GoDaddy / Namecheap / Google Domains first if you don't have one.
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              data-testid="domain-input"
              value={pendingDomain || domain.pending_domain || ""}
              onChange={(e) => setPendingDomain(e.target.value)}
              placeholder="e.g. myfashion.com"
              className="flex-1 border border-[hsl(var(--kb-border))] bg-white px-3 py-2 text-sm"
            />
            <button
              data-testid="domain-save-btn"
              onClick={saveDomain}
              disabled={busy || !pendingDomain}
              className="px-4 py-2 bg-[hsl(var(--kb-accent))] text-white text-sm disabled:opacity-50"
            >{busy ? "Saving…" : "Connect domain"}</button>
          </div>

          {domain.status === "pending_dns" && domain.verification && (
            <div className="mt-4 p-4 border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] space-y-3">
              <div className="text-sm font-medium">Step 2 — Add this TXT record at your DNS provider</div>
              <div className="space-y-2 text-sm">
                <Field label="Type" value="TXT" mono={false} copy={false} />
                <Field label="Host / Name" value={`${domain.verification.txt_record_host}.${domain.pending_domain}`} />
                <Field label="Value" value={domain.verification.txt_record_value} />
              </div>
              <div className="text-sm">
                Also create a <strong>CNAME</strong> on <code className="text-xs bg-white px-1.5 py-0.5">@</code> (root) or <code className="text-xs bg-white px-1.5 py-0.5">www</code> →&nbsp;
                <span className="font-mono text-xs bg-white px-1.5 py-0.5">{domain.verification.cname_target}</span>
              </div>
              <div className="text-xs text-[hsl(var(--kb-text-muted))]">
                DNS propagation takes 5-30 minutes. Once added, click Verify.
              </div>
              <button
                data-testid="domain-verify-btn"
                onClick={verify}
                disabled={verifying}
                className="px-4 py-2 bg-[hsl(var(--kb-accent))] text-white text-sm disabled:opacity-50"
              >{verifying ? <><Loader2 className="inline animate-spin w-3 h-3 mr-1" />Checking…</> : "Verify & activate"}</button>
            </div>
          )}
        </div>
      </section>

      {/* ---------------- MOBILE APP ---------------- */}
      <section className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface))] p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <Smartphone className="w-5 h-5" />
          <h2 className="text-xl">Native mobile app (Play Store + App Store)</h2>
        </div>
        <p className="text-sm text-[hsl(var(--kb-text-muted))] mb-5">
          The fastest path is{" "}
          <a href="https://www.pwabuilder.com" target="_blank" rel="noopener noreferrer"
             className="underline text-[hsl(var(--kb-primary))]">PWABuilder.com</a>{" "}— it generates both an Android AAB (Play Store) and an iOS package from your storefront URL. After PWABuilder builds the Android app, paste the <code>assetlinks.json</code> it gives you below; the platform will start serving it at <code>/.well-known/assetlinks.json</code> on your domain immediately, which is what Google requires for the install banner to verify.
        </p>

        {/* Quick-launch row to PWABuilder pre-filled with this storefront */}
        <div className="border border-[hsl(var(--kb-border))] bg-white p-4 mb-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="pwabuilder-launch">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-text-muted))] mb-1">Storefront URL PWABuilder will scan</div>
            <div className="text-sm font-mono break-all">{storefrontOrigin(ctx, domain)}</div>
          </div>
          <a
            href={`https://www.pwabuilder.com/reportcard?site=${encodeURIComponent(storefrontOrigin(ctx, domain))}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="pwabuilder-open-btn"
            className="px-4 py-2 bg-[hsl(var(--kb-accent))] text-white text-sm inline-flex items-center gap-2 whitespace-nowrap"
          >
            <ExternalLink className="w-4 h-4" /> Open in PWABuilder
          </a>
        </div>

        {/* Brand-logo upload reminder: without a real PWA icon, PWABuilder
            falls back to /favicon.ico (16x16) and the generated app icon
            looks terrible. Detect-and-warn here links to where they upload it. */}
        <div className="border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-900 mb-5 flex items-start gap-2" data-testid="pwa-logo-reminder">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <b>Before you build:</b> upload a square logo (512×512 recommended) at{" "}
            <a href="/admin/settings" className="underline">Settings → Branding</a>. Without it, PWABuilder uses the platform's tiny <code>/favicon.ico</code> as the app icon and the Play Store install banner looks broken.
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <label className="text-sm">
            <div className="mb-1 text-[hsl(var(--kb-text-muted))]">Android package id</div>
            <input
              data-testid="pwa-android-pkg"
              value={pwaForm.android_package_name || ""}
              onChange={(e) => setPwaForm({ ...pwaForm, android_package_name: e.target.value })}
              placeholder={`com.kalkibazaar.${ctx.slug.replace(/-/g, "_")}`}
              className="w-full border border-[hsl(var(--kb-border))] bg-white px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="text-sm">
            <div className="mb-1 text-[hsl(var(--kb-text-muted))]">Android release SHA-256 (optional — only used if no assetlinks.json uploaded)</div>
            <input
              data-testid="pwa-android-sha"
              value={pwaForm.android_sha256_fingerprint || ""}
              onChange={(e) => setPwaForm({ ...pwaForm, android_sha256_fingerprint: e.target.value })}
              placeholder="AA:BB:CC:DD:..."
              className="w-full border border-[hsl(var(--kb-border))] bg-white px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="text-sm">
            <div className="mb-1 text-[hsl(var(--kb-text-muted))]">iOS Team ID (optional)</div>
            <input
              data-testid="pwa-ios-team"
              value={pwaForm.ios_team_id || ""}
              onChange={(e) => setPwaForm({ ...pwaForm, ios_team_id: e.target.value })}
              placeholder="ABCD123456"
              className="w-full border border-[hsl(var(--kb-border))] bg-white px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="text-sm">
            <div className="mb-1 text-[hsl(var(--kb-text-muted))]">iOS Bundle ID (optional)</div>
            <input
              data-testid="pwa-ios-bundle"
              value={pwaForm.ios_bundle_id || ""}
              onChange={(e) => setPwaForm({ ...pwaForm, ios_bundle_id: e.target.value })}
              placeholder={`com.kalkibazaar.${ctx.slug.replace(/-/g, "")}`}
              className="w-full border border-[hsl(var(--kb-border))] bg-white px-3 py-2 text-sm font-mono"
            />
          </label>
          <label className="text-sm">
            <div className="mb-1 text-[hsl(var(--kb-text-muted))]">Theme color</div>
            <input
              data-testid="pwa-theme"
              type="color"
              value={pwaForm.theme_color || "#111111"}
              onChange={(e) => setPwaForm({ ...pwaForm, theme_color: e.target.value })}
              className="w-full h-10 border border-[hsl(var(--kb-border))] bg-white"
            />
          </label>
          <label className="text-sm">
            <div className="mb-1 text-[hsl(var(--kb-text-muted))]">Splash color</div>
            <input
              data-testid="pwa-splash"
              type="color"
              value={pwaForm.splash_color || "#ffffff"}
              onChange={(e) => setPwaForm({ ...pwaForm, splash_color: e.target.value })}
              className="w-full h-10 border border-[hsl(var(--kb-border))] bg-white"
            />
          </label>
        </div>

        {/* Raw .well-known uploads */}
        <div className="mt-6 space-y-5">
          {isSharedHost(domain) && (
            <div className="border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] p-3 text-xs flex gap-2" data-testid="shared-host-warning">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-[hsl(var(--kb-text-muted))]" />
              <div>
                <b>Shared platform host.</b> Your storefront lives on the platform's domain (no custom domain connected yet), so the <code>/.well-known/assetlinks.json</code> file is shared with every other tenant on this host. Your entry is automatically merged into the combined file — that's how Google verifies multiple TWAs on the same host. Connect a custom domain in the section above for a fully isolated verification file.
                <div className="mt-2 text-[11px]">
                  <b>Production note:</b> After saving, the platform needs a quick redeploy to publish the change to <code>https://&lt;host&gt;/.well-known/...</code>. Use the <b>Verify live</b> button below to confirm Android/Apple can read your entry.
                </div>
              </div>
            </div>
          )}
          <WellKnownEditor
            label="assetlinks.json"
            hint="Paste the file PWABuilder generates when you build the Android app. Required by Google so the Play Store install banner verifies your domain owns the package."
            value={pwaForm.assetlinks_json || ""}
            onChange={(v) => setPwaForm({ ...pwaForm, assetlinks_json: v })}
            servedAt={`${verificationApex(domain)}/.well-known/assetlinks.json`}
            testIdPrefix="assetlinks"
          />
          <WellKnownEditor
            label="apple-app-site-association"
            hint="Paste the AASA file PWABuilder generates with your iOS package. Required for iOS universal links."
            value={pwaForm.apple_aasa_json || ""}
            onChange={(v) => setPwaForm({ ...pwaForm, apple_aasa_json: v })}
            servedAt={`${verificationApex(domain)}/.well-known/apple-app-site-association`}
            testIdPrefix="aasa"
          />
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <button
            data-testid="pwa-save-btn"
            onClick={savePwa}
            disabled={busy}
            className="px-4 py-2 border border-[hsl(var(--kb-border))] text-sm bg-white hover:bg-[hsl(var(--kb-surface-alt))]"
          >Save settings</button>
          <button
            data-testid="pwa-bundle-btn"
            onClick={downloadBundle}
            className="px-4 py-2 border border-[hsl(var(--kb-border))] text-sm bg-white hover:bg-[hsl(var(--kb-surface-alt))] inline-flex items-center gap-2"
            title="Advanced: download a ZIP with Bubblewrap & PWABuilder configs and a README, for CLI workflows."
          ><Download className="w-4 h-4" />Advanced: download bundle ZIP</button>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** URL where PWABuilder should scan and where the storefront actually
 *  renders — includes `/t/{slug}` on the shared platform host. */
function storefrontOrigin(ctx, domain) {
  const custom = domain?.custom_domain && domain?.status === "active" ? domain.custom_domain : null;
  if (custom) return custom.startsWith("http") ? custom : `https://${custom}`;
  return `${window.location.origin}/t/${ctx?.slug || "default"}`;
}

/** Apex origin where Android/Apple actually fetch the `.well-known/...`
 *  verification files. The TWA spec ignores subpaths, so this is always
 *  the bare host — `/t/{slug}` is NEVER part of the verification URL. */
function verificationApex(domain) {
  const custom = domain?.custom_domain && domain?.status === "active" ? domain.custom_domain : null;
  if (custom) {
    const root = custom.startsWith("http") ? custom : `https://${custom}`;
    return root.replace(/\/+$/, "");
  }
  return window.location.origin;
}

/** Is this the shared platform host (no custom domain attached)? On the
 *  shared host all tenants' assetlinks are merged into one file. */
function isSharedHost(domain) {
  return !(domain?.custom_domain && domain?.status === "active");
}
